import { GameState, TeamInLeague, League, Player, Fixture, ManagerHistoryEntry, CupFixture, NewsItem, ClubHistory, Result, Morale, DisplayFixture, PlayerHistoryRecord, GameConfig } from '../domain/entities';
import { getPlayerWeeklyWage, getPlayerValue } from '../domain/constants';
import { simulateMatch } from './MatchSimulator';
import { setDefaultTactics } from './TeamService';
import { updateAITransferLists, performAITransfers } from './AITransferService';
import { refreshYouthSquad } from './YouthDevelopmentService';
import { CupService } from './CupService';
import { progressToNextSeason, generateTeamObjectives } from './simulation/seasonal';
import { runWeeklySim } from './simulation/weekly';
import { AssistantManagerBriefingService } from './AssistantManagerBriefingService';
import { generateSeasonSchedule } from './SeasonScheduler';
import { assignTeamBadges } from '../utils/badgeUtils';
import { FixturesHelper } from './FixturesHelper';

export const addNewsItem = (state: GameState, title: string, message: string, type: NewsItem['type'], playerRetirementData?: NewsItem['playerRetirementData'], link?: NewsItem['link'], fixtureData?: DisplayFixture): void => {
    state.news.unshift({
        id: `${state.season}-${state.currentWeek}-${Date.now()}-${Math.random()}`,
        season: state.season,
        week: state.currentWeek,
        title,
        message,
        type,
        isRead: false,
        playerRetirementData,
        link,
        fixtureData,
    });
};

export const isPlayerRecruitmentBlocked = (player: Player, currentSeason: number, currentWeek: number, gameConfig: GameConfig): { blocked: boolean; weeksRemaining: number } => {
    if (!player.recruitmentRejections?.blockedUntil) {
        return { blocked: false, weeksRemaining: 0 };
    }

    const blockedUntil = player.recruitmentRejections.blockedUntil;
    const weeksPerSeason = gameConfig.WEEKS_PER_SEASON;

    // Calculate total weeks for comparison
    const currentTotalWeeks = currentSeason * weeksPerSeason + currentWeek;
    const blockedUntilTotalWeeks = blockedUntil.season * weeksPerSeason + blockedUntil.week;

    if (currentTotalWeeks >= blockedUntilTotalWeeks) {
        // Block period has expired
        return { blocked: false, weeksRemaining: 0 };
    }

    const weeksRemaining = blockedUntilTotalWeeks - currentTotalWeeks;
    return { blocked: true, weeksRemaining };
};

const populateInitialTransferList = (gameState: GameState): void => {
    const listedIds = new Set<number>();

    Object.values(gameState.leagues).forEach(league => {
        league.teams.forEach(team => {
            if (team.name !== gameState.playerTeamName && team.squad.length > 18) {
                const sortedSquad = [...team.squad].sort((a, b) => a.skill - b.skill);
                const playerToList = sortedSquad[Math.floor(Math.random() * (sortedSquad.length / 2))];
                if (playerToList) {
                    listedIds.add(playerToList.id);
                }
            }
        });
    });

    gameState.playersForSale = Array.from(listedIds);
};


export const createNewGame = (gameData: any): GameState => {
    const leagues: { [key: string]: League } = JSON.parse(JSON.stringify(gameData.leagues));
    let fixtures = FixturesHelper.initializeEmptyFixtures();

    // Set default tactics for all teams and assign unique badges
    const allTeams: TeamInLeague[] = [];
    Object.values(leagues).forEach((league: League) => {
        league.teams.forEach(team => {
            setDefaultTactics(team);
            allTeams.push(team);
        });
    });

    // Assign unique team badges for visual identity
    assignTeamBadges(allTeams);

    const leagueNames = gameData.config.LEAGUE_NAMES;
    const playerLeagueName = leagueNames[leagueNames.length - 1];
    const playerTeamName = leagues[playerLeagueName].teams[Math.floor(Math.random() * leagues[playerLeagueName].teams.length)].name;

    const initialState: GameState = {
        version: gameData.version,
        gameStarted: true,
        season: 1,
        currentWeek: 1,
        playerTeamName,
        playerLeagueName,
        leagues,
        fixtures,
        cups: {},
        playersForSale: [],
        gameOver: false,
        managerHistory: [],
        transferHistory: [],
        news: [],
        incomingBids: [],
        freeAgents: [],
        gameType: gameData.gameType,
        config: gameData.config,
        debugFlags: {
            adsDisabled: false,
            moneyAdded: false,
            simulationUsed: false,
            aiPlayerUsed: false,
            alwaysWin: false,
            showHiddenInfo: false,
        },
        managerName: 'Manager',
        seasonHistory: [],
    };

    const cupNames = gameData.config.CUP_NAMES;

    // Create cup objects (scheduler will assign their weeks)
    const cupsForScheduler: { [cupName: string]: any } = {};
    cupNames.forEach(name => {
        const cup = CupService.createInitialCup(name, initialState.leagues, initialState.gameType, initialState.config);
        initialState.cups[name] = cup;
        cupsForScheduler[name] = cup;
    });

    // Use the season scheduler to generate all fixtures with proper week assignments
    const leagueTeamNames: { [leagueName: string]: string[] } = {};
    Object.values(initialState.leagues).forEach((league: League) => {
        leagueTeamNames[league.name] = league.teams.map(t => t.name);
    });

    const { fixtures: scheduledFixtures, cupWeeks: schedulerCupWeeks, cupRoundWeeks } = generateSeasonSchedule(cupsForScheduler, leagueTeamNames, cupNames);

    // Update cup rounds with their scheduled weeks from the scheduler BEFORE generating fixtures
    cupNames.forEach(cupName => {
        const cup = initialState.cups[cupName];
        if (cup && cup.rounds) {
            cup.rounds.forEach((round, index) => {
                if (cupRoundWeeks[cupName] && cupRoundWeeks[cupName][index] !== undefined) {
                    round.week = cupRoundWeeks[cupName][index];
                }
            });
        }
    });

    // Initialize empty weeks for all cup rounds (reserves weeks even before fixtures are determined)
    fixtures = FixturesHelper.initializeCupRoundWeeks(fixtures, initialState.cups);

    // Now generate first round fixtures with correct week assignments and store in GameState.fixtures.cups
    const cupFixturesByName: { [cupName: string]: CupFixture[] } = {};
    cupNames.forEach(name => {
        const cup = initialState.cups[name];
        const firstRoundFixtures = CupService.generateRoundFixtures(cup);
        cupFixturesByName[name] = firstRoundFixtures;
        // Add cup fixtures to GameState.fixtures.cups
        fixtures = FixturesHelper.addCupFixtures(fixtures, name, cup.rounds[0].week, firstRoundFixtures);
    });

    // Build complete set of cup weeks from cupRoundWeeks (all rounds, not just first)
    const allCupWeeks = new Set<number>();
    cupNames.forEach(cupName => {
        if (cupRoundWeeks[cupName]) {
            Object.values(cupRoundWeeks[cupName]).forEach(week => {
                allCupWeeks.add(week);
            });
        }
    });

    // Add league fixtures (organized by week), but skip weeks reserved for any cup round
    Object.entries(scheduledFixtures).forEach(([leagueName, leagueFixtures]) => {
        // Group fixtures by week
        const fixturesByWeek: { [week: string]: Fixture[] } = {};
        leagueFixtures.forEach(fixture => {
            if (!allCupWeeks.has(fixture.week)) {
                if (!fixturesByWeek[fixture.week]) fixturesByWeek[fixture.week] = [];
                fixturesByWeek[fixture.week].push(fixture);
            }
        });
        // Add all week-based fixtures for this league (addLeagueFixtures handles adding to weeks automatically)
        Object.entries(fixturesByWeek).forEach(([week, weekFixtures]) => {
            fixtures = FixturesHelper.addLeagueFixtures(fixtures, leagueName, parseInt(week, 10), weekFixtures);
        });
    });

    // Note: Cup fixtures are already in fixtures.cups and fixtures.weeks.
    // They should NOT be added to fixtures.leagues as that creates duplicates when
    // display components combine league and cup fixtures.

    initialState.fixtures = fixtures;

    populateInitialTransferList(initialState);

    // Perform initial youth intake for all teams.
    Object.values(initialState.leagues).forEach(league => {
        league.teams.forEach(team => {
            refreshYouthSquad(team, league.name, initialState.config, initialState.currentWeek);
        });
    });

    // Generate initial season objectives for all teams
    Object.values(initialState.leagues).forEach(league => {
        league.teams.forEach(team => {
            team.seasonObjectives = generateTeamObjectives(team, league, leagueNames, initialState.config);
        });
    });

    addNewsItem(initialState, 'Youth Intake', 'The first batch of young players has arrived at your academy.', 'youth', undefined, { type: 'youth', title: 'Youth Academy' });

    // Generate initial briefing from Assistant Manager
    const playerTeam = initialState.leagues[initialState.playerLeagueName].teams.find(t => t.name === initialState.playerTeamName);
    const playerLeague = initialState.leagues[initialState.playerLeagueName];
    if (playerTeam && playerLeague) {
        const initialBriefing = AssistantManagerBriefingService.generateInitialBriefing(initialState, playerTeam, playerLeague, 'game_start');
        initialState.assistantManagerBriefingHistory = [initialBriefing];

        addNewsItem(initialState, '📧 Welcome from Assistant Manager', 'Your Assistant Manager has provided an initial briefing. Check the Assistant page to review.', 'general', undefined, { type: 'assistant', title: 'Assistant Manager' });
    }

    return initialState;
};

export const getPlayerTeam = (gameState: GameState): TeamInLeague | null => {
    const league = gameState.leagues[gameState.playerLeagueName];
    if (!league) {
        console.error(`League '${gameState.playerLeagueName}' not found in game state`);
        return null;
    }
    const team = league.teams.find(t => t.name === gameState.playerTeamName);
    if (!team) {
        console.error(`Team '${gameState.playerTeamName}' not found in league '${gameState.playerLeagueName}'`);
    }
    return team || null;
};

export const findTeamInState = (gameState: GameState, teamName: string): { team: TeamInLeague, leagueName: string } | null => {
    for (const leagueName in gameState.leagues) {
        const team = gameState.leagues[leagueName].teams.find(t => t.name === teamName);
        if (team) {
            return { team, leagueName };
        }
    }
    return null;
}

export const findPlayerInState = (gameState: GameState, playerId: number): { player: Player, team: TeamInLeague, leagueName: string } | null => {
    // Search youth players first
    const playerTeam = getPlayerTeam(gameState);
    const youthPlayer = playerTeam.youthAcademy.squad.find(p => p.id === playerId);
    if (youthPlayer) {
        return { player: youthPlayer, team: playerTeam, leagueName: gameState.playerLeagueName };
    }

    // Search senior squads
    for (const leagueName in gameState.leagues) {
        for (const team of gameState.leagues[leagueName].teams) {
            const player = team.squad.find(p => p.id === playerId);
            if (player) {
                return { player, team, leagueName };
            }
        }
    }
    return null;
}

// New function to find players anywhere including retired ones (for viewing only)
export const findPlayerAnywhere = (gameState: GameState, playerId: number): { player: Player, team: TeamInLeague | null, leagueName: string | null } | null => {
    // First try to find in active squads
    const activePlayerData = findPlayerInState(gameState, playerId);
    if (activePlayerData) {
        return activePlayerData;
    }

    // If not found, search free agents
    const freeAgent = gameState.freeAgents?.find(fa => fa.playerId === playerId);
    if (freeAgent && freeAgent.playerSnapshot) {
        return { player: freeAgent.playerSnapshot, team: null, leagueName: null };
    }

    // If not found, search retired players
    const retiredPlayer = gameState.retiredPlayers?.find(p => p.id === playerId);
    if (retiredPlayer) {
        return { player: retiredPlayer, team: null, leagueName: null };
    }

    return null;
}

// FIX: Corrected simulateWeek to match runWeeklySim signature and return type by removing unused onProgress parameter.
export const simulateWeek = async (gameState: GameState, isAiPunditEnabled: boolean): Promise<GameState> => {
    const { newState } = await runWeeklySim(gameState, isAiPunditEnabled);
    return newState;
};

export const advanceToNextSeason = (gameState: GameState): GameState => {
    return progressToNextSeason(gameState);
};

export const checkForSacking = (
    team: TeamInLeague, 
    league: League, 
    allLeagues: Record<string, League>,
    config: GameConfig
): { sacked: boolean, reason?: string, newTeamName?: string, newLeagueName?: string } => {
    
    let sackProbability = 0;
    
    // Performance-based
    const leagueRank = league.teams.findIndex(t => t.name === team.name);
    const leagueSize = league.teams.length;
    const leagueConfig = config.LEAGUE_CONFIG[league.name];
    if (leagueConfig.relegation > 0 && leagueRank >= leagueSize - leagueConfig.relegation) {
        sackProbability += 0.5; // High chance if in relegation zone at end of season
    }
    
    // Finance-based
    if (team.finances.balance < -10000000) {
        sackProbability += 0.4;
    } else if (team.finances.balance < -2000000) {
        sackProbability += 0.2;
    }
    
    // Confidence-based
    sackProbability += (50 - team.boardConfidence) / 100;

    if (Math.random() < sackProbability) {
        // Get the bottom league (lowest tier)
        const bottomLeagueIndex = config.LEAGUE_NAMES.length - 1;
        const bottomLeagueName = config.LEAGUE_NAMES[bottomLeagueIndex];
        const bottomLeague = allLeagues[bottomLeagueName];
        const bottomLeagueConfig = config.LEAGUE_CONFIG[bottomLeagueName];

        // Determine which teams to exclude:
        // 1. Teams being promoted (top of the league)
        const promotionZone = bottomLeagueConfig.promotion || 0;
        // 2. Teams being relegated (bottom of the league)
        const relegationZone = bottomLeagueConfig.relegation || 0;
        // 3. Teams that were just relegated FROM the league above (if bottom league is not the top league)
        const leagueAboveIndex = bottomLeagueIndex - 1;
        const teamsRelegatedThisSeason = leagueAboveIndex >= 0
            ? allLeagues[config.LEAGUE_NAMES[leagueAboveIndex]].teams
                .slice(-config.LEAGUE_CONFIG[config.LEAGUE_NAMES[leagueAboveIndex]].relegation)
                .map(t => t.name)
            : [];

        const potentialNewTeams = bottomLeague.teams.filter((t, index) => {
            // Exclude current team
            if (t.name === team.name) return false;
            // Exclude teams in promotion zone (top positions)
            if (promotionZone > 0 && index < promotionZone) return false;
            // Exclude teams in relegation zone (bottom positions)
            if (relegationZone > 0 && index >= bottomLeague.teams.length - relegationZone) return false;
            // Exclude teams that were just relegated from above
            if (teamsRelegatedThisSeason.includes(t.name)) return false;
            return true;
        });

        if (potentialNewTeams.length === 0) {
            // Fallback: use all teams in bottom league except those at extreme positions
            const fallbackTeams = bottomLeague.teams.filter((t, index) => {
                if (t.name === team.name) return false;
                if (promotionZone > 0 && index < promotionZone) return false;
                if (relegationZone > 0 && index >= bottomLeague.teams.length - relegationZone) return false;
                return true;
            });
            if (fallbackTeams.length === 0) {
                return { sacked: false }; // No valid team available
            }
            const newTeam = fallbackTeams[Math.floor(Math.random() * fallbackTeams.length)];
            let reason = "The board are disappointed with the club's performance this season.";
            if (team.finances.balance < -2000000) reason = "The board are concerned about the financial mismanagement of the club.";
            if (leagueRank >= leagueSize - leagueConfig.relegation && leagueConfig.relegation > 0) reason = "Relegation is unacceptable and the board have decided a change is needed.";
            return { sacked: true, reason, newTeamName: newTeam.name, newLeagueName: bottomLeagueName };
        }

        const newTeam = potentialNewTeams[Math.floor(Math.random() * potentialNewTeams.length)];

        let reason = "The board are disappointed with the club's performance this season.";
        if (team.finances.balance < -2000000) reason = "The board are concerned about the financial mismanagement of the club.";
        if (leagueRank >= leagueSize - leagueConfig.relegation && leagueConfig.relegation > 0) reason = "Relegation is unacceptable and the board have decided a change is needed.";

        return { sacked: true, reason, newTeamName: newTeam.name, newLeagueName: bottomLeagueName };
    }

    return { sacked: false };
};